export type LiveRtcStats = {
  type: string;
  id?: string;
  kind?: string;
  mediaType?: string;
  transportId?: string;
  selectedCandidatePairId?: string;
  availableOutgoingBitrate?: number;
  state?: string;
};

/**
 * Return the congestion estimate for the selected ICE path carrying video.
 * Old successful candidate pairs can remain in getStats() after a route
 * change; their estimates must not drive the publisher's quality ladder.
 */
export function selectedVideoAvailableOutgoingBitrate(stats: Iterable<LiveRtcStats>): number {
  const reports = Array.from(stats);
  const videoTransportIds = new Set(
    reports
      .filter(report => report.type === "outbound-rtp" && (report.kind || report.mediaType) === "video")
      .map(report => report.transportId)
      .filter((id): id is string => Boolean(id))
  );
  if (!videoTransportIds.size) return 0;

  const selectedPairIds = new Set(
    reports
      .filter(report => report.type === "transport" && report.id && videoTransportIds.has(report.id))
      .map(report => report.selectedCandidatePairId)
      .filter((id): id is string => Boolean(id))
  );
  if (!selectedPairIds.size) return 0;

  const estimates = reports
    .filter(report => report.type === "candidate-pair" && report.id && selectedPairIds.has(report.id) && report.state === "succeeded")
    .map(report => Number(report.availableOutgoingBitrate))
    .filter(value => Number.isFinite(value) && value > 0);

  // A missing estimate is not zero capacity: it means adaptation has no
  // trustworthy signal yet, so let WebRTC's built-in congestion controller
  // continue without incorrectly stepping the quality ladder.
  return estimates.length ? Math.min(...estimates) : 0;
}

export type AutoQualityProfile = { maxBitrate: number };

/** Keep publisher recovery alive for the whole capture session, with a bounded retry rate. */
export function publisherReconnectDelay(attempt: number): number {
  const normalizedAttempt = Math.max(0, Math.trunc(attempt));
  if (normalizedAttempt === 0) return 1_000;
  return Math.min(30_000, 2_000 * 2 ** Math.min(normalizedAttempt - 1, 4));
}

/** Step down quickly on congestion; only step up after a longer stable interval. */
export function nextAutoQualityIndex(
  profiles: readonly AutoQualityProfile[],
  currentIndex: number,
  availableBitrate: number,
  packetLossRatio: number,
  msSinceLastAdjustment: number,
  encoderLimited = false
): number {
  if (!profiles.length) return 0;
  const index = Math.max(0, Math.min(profiles.length - 1, Math.trunc(currentIndex)));
  const currentBitrate = profiles[index].maxBitrate;
  const congested = encoderLimited || packetLossRatio >= 0.03 || (availableBitrate > 0 && availableBitrate < currentBitrate * 0.8);
  if (congested) {
    return index > 0 && msSinceLastAdjustment >= 5_000 ? index - 1 : index;
  }
  const headroom = !encoderLimited && availableBitrate >= currentBitrate * 1.35 && packetLossRatio < 0.01;
  return headroom && msSinceLastAdjustment >= 20_000
    ? Math.min(profiles.length - 1, index + 1)
    : index;
}
